import { inject, Injectable, signal, computed } from '@angular/core';
import { ApiGatewayService, SakooResponse } from './api-gateway.service';
import { Observable, tap, map, of } from 'rxjs';

/**
 * Estructura de los datos devueltos al iniciar sesión con éxito.
 */
export interface LoginData {
  token: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly api = inject(ApiGatewayService);

  // Cargamos inicialmente el token desde sessionStorage (si existe)
  private readonly _token = signal<string | null>(sessionStorage.getItem('access_token'));

  // Exponemos el token e indicador de sesión como signals públicos de sólo lectura
  public readonly token = this._token.asReadonly();
  public readonly isAuthenticated = computed(() => !!this._token());

  /**
   * Realiza la petición de inicio de sesión administrativo.
   * Valida la regla de negocio: si el código retornado es diferente a 1000,
   * se arroja un error con el mensaje devuelto por el servidor.
   */
  login(email: string, password: string): Observable<LoginData> {
    return this.api.post<SakooResponse<LoginData>>('/api/backoffice/auth/login', { email, password }).pipe(
      map((res) => {
        // Regla: si el código no es 1000, es considerado un error de negocio
        if (res.code !== 1000) {
          throw {
            code: res.code,
            message: res.message || 'Correo electrónico o contraseña incorrectos',
            track_code: res.track_code,
          };
        }
        return res.data;
      }),
      tap((data) => {
        // En caso de éxito, persistimos de forma segura en sessionStorage y actualizamos el Signal
        if (data && data.token) {
          sessionStorage.setItem('access_token', data.token);
          this._token.set(data.token);
        }
      })
    );
  }

  /**
   * Cierra la sesión activa en el servidor e invalida las credenciales locales.
   */
  logout(): void {
    // Realiza la llamada HTTP al endpoint de logout (el gateway inyectará el token automáticamente si está en sessionStorage)
    this.api.post('/api/backoffice/auth/logout', {}).subscribe({
      next: () => console.log('Sesión cerrada exitosamente en el servidor.'),
      error: (err) => console.error('Error al notificar cierre de sesión al servidor:', err)
    });

    // Limpieza de datos en local de forma inmediata
    this.clearSession();
  }

  /**
   * Limpia las credenciales locales sin notificar al servidor.
   */
  clearSession(): void {
    sessionStorage.removeItem('access_token');
    this._token.set(null);
  }
}
